import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  text: string;
  path: string;
  adminOnly?: boolean;
  requiredPermission?: string;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems: NavItem[] = [
    {
      text: 'Dashboard',
      path: '/dashboard',
    },
    {
      text: 'Dokumentation',
      path: '/documentation',
    },
    {
      text: 'Analyse',
      path: '/analysis',
      requiredPermission: 'asm-manage-analytics',
    },
    {
      text: 'Admin',
      path: '/admin',
      adminOnly: true,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Top Navigation */}
      <header style={{
        backgroundColor: '#c91f0c', // FireHub primary red
        color: '#ffffff', // FireHub white
        padding: '0 20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '60px'
        }}>
          {/* Logo/Brand */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile ? (
              <img 
                src="/images/logo_square.png" 
                alt="ASMonitor Portal" 
                style={{ 
                  height: '40px', 
                  width: 'auto',
                  objectFit: 'contain'
                }} 
              />
            ) : (
              <img 
                src="/images/logo_landscape.png" 
                alt="ASMonitor Portal" 
                style={{ 
                  height: '30px', 
                  width: 'auto',
                  objectFit: 'contain'
                }} 
              />
            )}
          </div>

          {/* Navigation Menu */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {menuItems.map((item) => {
              // Check if user has admin access (super admin permission or specific permissions)
              const hasSuperAdminPermission = user?.permissions?.['firehub-super-admin'];
              const hasAdminPermissions = user?.permissions?.['asm-manage-users'] || user?.permissions?.['asm-manage-roles'] || user?.permissions?.['asm-settings-permission'];
              const isAdmin = user?.role === 'admin';

              const hasRequiredPermission = item.requiredPermission
                ? !!(user?.permissions as Record<string, boolean> | undefined)?.[item.requiredPermission]
                : true;
              
              if (item.adminOnly && !hasSuperAdminPermission && !hasAdminPermissions && !isAdmin) return null;
              if (item.requiredPermission && !hasSuperAdminPermission && !hasRequiredPermission) return null;
              
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.text}
                  onClick={() => navigate(item.path)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'transparent',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: isActive ? 'bold' : 'normal',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {item.text}
                </button>
              );
            })}

            {/* User Menu Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {user?.username || 'reviewer'} ▼
              </button>
              
              {dropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: '#ffffff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  minWidth: '150px',
                  zIndex: 1001
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #eee',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    {user?.fdName || user?.orgName || 'Unknown Org'}
                  </div>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      color: '#c91f0c',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main style={{ padding: '20px' }}>
        {children}
      </main>

      {/* Click outside to close dropdown */}
      {dropdownOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 